import type { FlatCategory } from "../flattenCategories";
import {
    MAX_SUGGESTIONS_PER_SECTION,
    type ProductSuggestion,
    type ProductSuggestionsStatus,
} from "@models/productSuggestion";

// The two kinds of row a shopper can highlight or click. Kept as one ordered list — categories
// first, then products — because the keyboard walks it by index (SearchPanel) and the DOM
// renders it by index (Autocomplete), and the two must never disagree about which row is which.
export type AutocompleteOption =
    | { kind: "category"; category: FlatCategory }
    | { kind: "product"; product: ProductSuggestion };

export const buildAutocompleteOptions = (
    query: string,
    suggestions: FlatCategory[],
    products: ProductSuggestion[],
): AutocompleteOption[] => {
    const lowered = query.toLowerCase();
    const categories = suggestions
        .filter((s) => s.displayName.toLowerCase().includes(lowered))
        .slice(0, MAX_SUGGESTIONS_PER_SECTION)
        .map((category): AutocompleteOption => ({ kind: "category", category }));
    // The server caps at `limit` too; sliced here as well so a payload that ignores it (or a
    // seeded atom) can never push the keyboard walk past what the section is meant to show.
    const productOptions = products
        .slice(0, MAX_SUGGESTIONS_PER_SECTION)
        .map((product): AutocompleteOption => ({ kind: "product", product }));
    return [...categories, ...productOptions];
};

// The id the combobox points aria-activedescendant at. Index-based: options are rebuilt on every
// keystroke, and the index is the one identity the keyboard and the DOM already share.
export const optionElementId = (index: number): string =>
    `search-autocomplete-option-${index}`;

// What the Products live region says. `visible` messages are the two a sighted shopper needs
// where the rows would be; the rest is screen-reader-only, because the rows already show it.
export interface ProductsLiveMessage {
    text: string;
    visible: boolean;
}

// `isPending` is SearchPanel's own knowledge that a request is armed or on the wire for the
// current query — it leads the hook's status by the debounce, which is why it, not the status
// atom alone, decides "Searching products…": the shopper must see the search start on the
// keystroke. The hook's `loading` counts as searching too (a re-request of a query the panel
// already has a verdict for). Searching with held rows says nothing (stale-while-revalidate:
// the rows stand in), and searching outranks a previous failure (a new search is under way).
export const productsLiveMessage = (
    isPending: boolean,
    status: ProductSuggestionsStatus,
    productCount: number,
): ProductsLiveMessage | null => {
    if ((isPending || status === "loading") && productCount === 0) {
        return { text: "Searching products…", visible: true };
    }
    if (status === "failed") {
        return { text: "Couldn't load products", visible: true };
    }
    if (status === "loaded" && !isPending) {
        const text =
            productCount === 0
                ? "No products"
                : productCount === 1
                    ? "1 product"
                    : `${productCount} products`;
        return { text, visible: false };
    }
    return null;
};
